import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from './errors';

/**
 * O efetivo e o equipamento do dia (Fase 18, fatia 4).
 *
 * ```text
 * Equipe Envolvida
 * Código  Origem  Descrição              Atribuição           Qtd. Utilizada
 * Tipo            ENGENHEIRO CIVIL       Mão de obra direta   1
 * Tipo            PEDREIRO               Mão de obra direta   2
 *
 * Equipamento Utilizado
 * Código  Origem  Descrição                        Status   Qtd. Utilizada
 * Tipo            SERRA CIRCULAR - MAKITA 5007N    Em uso   3
 * ```
 *
 * ## O efetivo é contado e corrigido, e só o corrigido vai ao papel
 *
 * `ENGENHEIRO CIVIL 1` e `TÉCNICO EM SEGURANÇA DO TRABALHO 1` estão no RDO de referência e
 * não executam frente nenhuma — não têm alocação, não têm conferência, e uma contagem
 * automática os apagaria. Contar por funcionário ativo traria o oposto: quem faltou.
 *
 * Então o sistema **conta quem foi conferido no dia** e oferece o número; quem relata
 * corrige e acrescenta as funções que não executam frente. O contado **não é guardado** —
 * ele é recalculado a cada leitura, pela mesma razão que as frentes são (ADR-122): o que
 * se guarda é o que alguém digitou, e o derivado se deriva. A diferença entre os dois
 * continua visível, e ela é informação: um corrigido sistematicamente maior que o contado
 * é gente no canteiro que o sistema não conhece.
 *
 * ## O equipamento tem status porque máquina parada é fato do dia
 *
 * O RDO de referência só usa `Em uso`, e uma coluna com um valor só não seria coluna. Mas
 * guindaste parado é exatamente o tipo de fato que o documento existe para registrar — é o
 * mesmo raciocínio do `workable` do turno (RN-215): o que aconteceu e o que isso custou
 * são duas informações, e o documento carrega as duas ou não serve de prova.
 */

/** O que alguém digitou: a contagem corrigida de uma função. */
export interface CrewCount {
  readonly roleName: string;
  readonly quantity: number;
}

/** A linha do papel: o contado, o corrigido, e o que vale. */
export interface CrewLine {
  readonly roleName: string;
  /** Quantos o sistema contou nas conferências do dia. */
  readonly counted: number;
  /** Quantos quem relatou disse que estavam. Ausente = ninguém corrigiu. */
  readonly reported?: number;
  /** `reported ?? counted` — é este que vai ao papel. */
  readonly quantity: number;
}

export type EquipmentStatus = 'IN_USE' | 'IDLE' | 'MAINTENANCE';

export const EQUIPMENT_STATUSES: readonly EquipmentStatus[] = ['IN_USE', 'IDLE', 'MAINTENANCE'];

export const EQUIPMENT_STATUS_LABEL: Readonly<Record<EquipmentStatus, string>> = {
  IN_USE: 'Em uso',
  IDLE: 'Parado',
  MAINTENANCE: 'Em manutenção',
};

export interface EquipmentLine {
  /** O código do equipamento na obra, quando ela usa um. */
  readonly code?: string;
  readonly description: string;
  readonly status: EquipmentStatus;
  readonly quantity: number;
}

export const MAX_CREW_ROLES = 40;
export const MAX_EQUIPMENT_LINES = 60;
export const MAX_ROLE_NAME = 120;
export const MAX_EQUIPMENT_DESCRIPTION = 200;
export const MAX_EQUIPMENT_CODE = 40;
/** Ninguém tem mil pedreiros num dia; o que passa disso é tecla presa, não efetivo. */
export const MAX_QUANTITY = 999;

/**
 * Conta por função quem teve o dia conferido.
 *
 * Recebe uma função por pessoa presente — a mesma granularidade da conferência — e devolve
 * a contagem por nome de função, em ordem alfabética. Quem chama decide **quem** entra
 * (RN-216: conferido e não ausente); aqui só se conta.
 */
export function countCrewByRole(roleNames: readonly string[]): readonly CrewLine[] {
  const porFuncao = new Map<string, number>();
  for (const nome of roleNames) {
    const limpo = nome.trim();
    if (limpo === '') continue;
    porFuncao.set(limpo, (porFuncao.get(limpo) ?? 0) + 1);
  }

  return [...porFuncao.entries()]
    .map(([roleName, counted]) => ({ roleName, counted, quantity: counted }))
    .sort((a, b) => a.roleName.localeCompare(b.roleName, 'pt-BR'));
}

/**
 * Junta o contado com o corrigido (RN-212).
 *
 * A função corrigida que o sistema não contou entra com `counted: 0` — é o engenheiro do
 * RDO de referência, e o zero é a afirmação de que ele não apareceu em conferência
 * nenhuma, não um erro. Vale sempre o corrigido quando ele existe, inclusive quando é
 * **zero**: dizer "hoje não tinha pedreiro" é uma correção, não um campo em branco.
 */
export function mergeCrew(
  counted: readonly CrewLine[],
  reported: readonly CrewCount[],
): readonly CrewLine[] {
  const corrigido = new Map(reported.map((item) => [item.roleName, item.quantity]));
  const linhas = new Map<string, CrewLine>();

  for (const linha of counted) {
    const digitado = corrigido.get(linha.roleName);
    linhas.set(linha.roleName, {
      roleName: linha.roleName,
      counted: linha.counted,
      quantity: digitado ?? linha.counted,
      ...(digitado === undefined ? {} : { reported: digitado }),
    });
  }

  for (const [roleName, quantity] of corrigido) {
    if (linhas.has(roleName)) continue;
    linhas.set(roleName, { roleName, counted: 0, reported: quantity, quantity });
  }

  return [...linhas.values()].sort((a, b) => a.roleName.localeCompare(b.roleName, 'pt-BR'));
}

function refuse(code: string, details?: Record<string, unknown>): Result<never, DomainError> {
  return err(domainError(code, PLANNING_ERROR_MESSAGE[code]!, details));
}

function quantidadeValida(valor: number): boolean {
  return Number.isInteger(valor) && valor >= 0 && valor <= MAX_QUANTITY;
}

/**
 * Valida o efetivo corrigido.
 *
 * Linha sem função é descartada, como o turno vazio da fatia 2 e a tarefa em branco da 3:
 * a tela mostra campos para preencher, e quem deixou um em branco não errou.
 */
export function parseCrewCounts(
  entradas: readonly CrewCount[],
): Result<readonly CrewCount[], DomainError> {
  const linhas: CrewCount[] = [];
  const vistas = new Set<string>();

  for (const entrada of entradas) {
    const roleName = entrada.roleName.trim();
    if (roleName === '') continue;

    if (roleName.length > MAX_ROLE_NAME) {
      return refuse(PLANNING_ERROR.CREW_ROLE_TOO_LONG, { roleName: roleName.slice(0, 40) });
    }
    // Duas linhas para a mesma função são duas verdades sobre o mesmo número, e o papel
    // mostraria as duas sem dizer qual vale.
    if (vistas.has(roleName)) {
      return refuse(PLANNING_ERROR.CREW_ROLE_DUPLICATED, { roleName });
    }
    if (!quantidadeValida(entrada.quantity)) {
      return refuse(PLANNING_ERROR.CREW_QUANTITY_INVALID, {
        roleName,
        quantity: entrada.quantity,
      });
    }

    vistas.add(roleName);
    linhas.push({ roleName, quantity: entrada.quantity });
  }

  if (linhas.length > MAX_CREW_ROLES) {
    return refuse(PLANNING_ERROR.CREW_TOO_MANY, { count: linhas.length, max: MAX_CREW_ROLES });
  }
  return ok(linhas);
}

/** Valida o equipamento. Mesmo tratamento do vazio: linha sem descrição é descartada. */
export function parseEquipment(
  entradas: readonly EquipmentLine[],
): Result<readonly EquipmentLine[], DomainError> {
  const linhas: EquipmentLine[] = [];

  for (const entrada of entradas) {
    const description = entrada.description.trim();
    const code = entrada.code?.trim() ?? '';
    if (description === '') continue;

    if (description.length > MAX_EQUIPMENT_DESCRIPTION) {
      return refuse(PLANNING_ERROR.EQUIPMENT_DESCRIPTION_TOO_LONG, {
        description: description.slice(0, 40),
      });
    }
    if (code.length > MAX_EQUIPMENT_CODE) {
      return refuse(PLANNING_ERROR.EQUIPMENT_CODE_TOO_LONG, { code: code.slice(0, 40) });
    }
    // Zero equipamento em uso não é linha de equipamento: é a linha que sobrou de ontem e
    // hoje não vale. Quem não usou a serra apaga a linha, e o RDO não a imprime.
    if (!quantidadeValida(entrada.quantity) || entrada.quantity < 1) {
      return refuse(PLANNING_ERROR.EQUIPMENT_QUANTITY_INVALID, {
        description: description.slice(0, 40),
        quantity: entrada.quantity,
      });
    }

    linhas.push({
      description,
      status: entrada.status,
      quantity: entrada.quantity,
      ...(code === '' ? {} : { code }),
    });
  }

  if (linhas.length > MAX_EQUIPMENT_LINES) {
    return refuse(PLANNING_ERROR.EQUIPMENT_TOO_MANY, {
      count: linhas.length,
      max: MAX_EQUIPMENT_LINES,
    });
  }
  return ok(linhas);
}

/** `SERRA CIRCULAR · Em uso · 3` — a linha do equipamento como ela sai no papel. */
export function describeEquipment(line: EquipmentLine): string {
  const partes = [line.description, EQUIPMENT_STATUS_LABEL[line.status], String(line.quantity)];
  return partes.join(' · ');
}
